import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentIcon,
  CheckCircleIcon, XCircleIcon, MinusCircleIcon,
  ArrowLeftIcon, ExclamationTriangleIcon,
  ClockIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

// ── History helpers ───────────────────────────────────────────────────────────

const HISTORY_KEY = 'bookmarker_io_history';
const MAX_HISTORY = 50;

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; }
  catch { return []; }
}

function saveHistoryEntry(entry) {
  const history = loadHistory();
  history.unshift({ ...entry, timestamp: new Date().toISOString() });
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

function clearHistory() {
  localStorage.removeItem(HISTORY_KEY);
}

function formatTimestamp(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function todaySlug() {
  return new Date().toISOString().slice(0, 10);
}

function buildJson(bookmarks) {
  const items = bookmarks.map(b => ({
    url: b.url,
    title: b.title || '',
    description: b.description || '',
    summary: b.summary || '',
    tags: b.tags || [],
    categories: b.categories || [],
    createdAt: b.createdAt,
  }));
  return JSON.stringify({ version: '1.0', exportedAt: new Date().toISOString(), count: items.length, bookmarks: items }, null, 2);
}

function buildHtml(bookmarks) {
  const rows = bookmarks.map(b => {
    const addDate = b.createdAt ? Math.floor(new Date(b.createdAt).getTime() / 1000) : '';
    const tags = (b.tags || []).join(',');
    const desc = b.description ? `\n    <DD>${escapeHtml(b.description)}` : '';
    return `    <DT><A HREF="${escapeHtml(b.url)}"${addDate ? ` ADD_DATE="${addDate}"` : ''}${tags ? ` TAGS="${escapeHtml(tags)}"` : ''}>${escapeHtml(b.title || b.url)}</A>${desc}`;
  }).join('\n');
  return `<!DOCTYPE NETSCAPE-Bookmark-file-1>\n<!-- Exported from BookMarker on ${todaySlug()} -->\n<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">\n<TITLE>BookMarker Bookmarks</TITLE>\n<H1>BookMarker</H1>\n<DL><p>\n${rows}\n</DL><p>\n`;
}

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Parse Netscape HTML bookmark file, optionally tagging items with their folder name.
// Returns items with a `folder` field (the immediate containing folder name, or '' for root).
//
// Strategy: query ALL <a href> elements regardless of DOM structure (DOMParser restructures
// the Netscape format's malformed HTML unpredictably), then determine each bookmark's folder
// by walking up to the nearest enclosing <dl> and checking how that DL was labelled.
function parseNetscapeHtml(text, addFolderTags) {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const results = [];

  // Given a DL element, return the folder name that labels it.
  // The label H3 may be in a DT that is either the DL's parent (parser put DL inside DT)
  // or a preceding sibling of the DL (standard sibling structure).
  function dlFolderName(dl) {
    const parent = dl.parentElement;
    if (parent?.tagName.toUpperCase() === 'DT') {
      const h3 = parent.querySelector('h3');
      if (h3) return h3.textContent.trim();
    }
    let sib = dl.previousElementSibling;
    while (sib) {
      if (sib.tagName.toUpperCase() === 'DT') {
        const h3 = sib.querySelector('h3');
        if (h3) return h3.textContent.trim();
      }
      sib = sib.previousElementSibling;
    }
    return '';
  }

  doc.querySelectorAll('a[href]').forEach(a => {
    const url = a.getAttribute('href') || '';
    if (!url.startsWith('http')) return;

    const title = a.textContent.trim();
    const tagsAttr = a.getAttribute('tags') || a.getAttribute('TAGS') || '';
    const tags = tagsAttr ? tagsAttr.split(',').map(t => t.trim()).filter(Boolean) : [];

    // Folder = label of the innermost enclosing DL
    const dl = a.closest('dl');
    const folder = dl ? dlFolderName(dl) : '';

    if (addFolderTags && folder) {
      const folderTag = folder.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (folderTag && !tags.includes(folderTag)) tags.push(folderTag);
    }

    // Description lives in a DD that follows the containing DT
    let description = '';
    const dt = a.closest('dt');
    if (dt) {
      const next = dt.nextElementSibling;
      if (next?.tagName.toUpperCase() === 'DD') description = next.textContent.trim();
    }

    results.push({ url, title, description, tags, folder });
  });

  return results;
}

function parseJsonFile(text) {
  let parsed;
  try { parsed = JSON.parse(text); } catch { return null; }
  // Accept {bookmarks: [...]} (BookMarker format) or a plain array
  const arr = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.bookmarks) ? parsed.bookmarks : null);
  if (!arr) return null;
  return arr
    .filter(item => typeof item?.url === 'string' && item.url.startsWith('http'))
    .map(item => ({
      url: item.url,
      title: item.title || '',
      description: item.description || '',
      tags: Array.isArray(item.tags) ? item.tags : [],
    }));
}

// ── Component ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'export', label: 'Export', icon: <ArrowDownTrayIcon className="h-4 w-4" /> },
  { id: 'import', label: 'Import', icon: <ArrowUpTrayIcon className="h-4 w-4" /> },
  { id: 'history', label: 'History', icon: <ClockIcon className="h-4 w-4" /> },
];

const ImportExport = () => {
  const [tab, setTab] = useState('export');

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/bookmarks" className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-5">
        <ArrowLeftIcon className="h-4 w-4" /> Back to Bookmarks
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Import / Export</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Back up your bookmarks or bring in links from another browser or app.</p>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-8">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'export' && <ExportTab />}
      {tab === 'import' && <ImportTab />}
      {tab === 'history' && <HistoryTab />}
    </div>
  );
};

// ── Export Tab ────────────────────────────────────────────────────────────────

const ExportTab = () => {
  const [status, setStatus] = useState('idle'); // idle | fetching | done | error
  const [error, setError] = useState(null);

  const handleExport = async (format) => {
    setStatus('fetching');
    setError(null);
    try {
      const bookmarks = await bookmarkService.getAllBookmarks();
      if (bookmarks.length === 0) {
        setError('You have no bookmarks to export.');
        setStatus('idle');
        return;
      }
      const filename = `bookmarker-export-${todaySlug()}.${format}`;
      if (format === 'json') {
        downloadFile(buildJson(bookmarks), filename, 'application/json');
      } else {
        downloadFile(buildHtml(bookmarks), filename, 'text/html');
      }
      saveHistoryEntry({ type: 'export', format, count: bookmarks.length });
      setStatus('done');
      setTimeout(() => setStatus('idle'), 3000);
    } catch (err) {
      setError(err.message || 'Export failed. Please try again.');
      setStatus('idle');
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Download all your bookmarks in your preferred format. Both formats include your titles, descriptions, and tags.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ExportCard
          title="BookMarker JSON"
          description="Full backup including AI summaries and categories. Best for restoring to BookMarker later."
          ext=".json"
          disabled={status === 'fetching'}
          onClick={() => handleExport('json')}
        />
        <ExportCard
          title="Browser HTML"
          description="Netscape format compatible with Chrome, Firefox, and Safari bookmark import."
          ext=".html"
          disabled={status === 'fetching'}
          onClick={() => handleExport('html')}
        />
      </div>

      {status === 'fetching' && (
        <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
          Fetching your bookmarks…
        </div>
      )}
      {status === 'done' && (
        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
          <CheckCircleIcon className="h-4 w-4" /> Export downloaded successfully.
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
          <ExclamationTriangleIcon className="h-4 w-4" /> {error}
        </div>
      )}
    </div>
  );
};

const ExportCard = ({ title, description, ext, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
  >
    <div className="flex items-start justify-between mb-3">
      <DocumentIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 transition-colors" />
      <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">{ext}</span>
    </div>
    <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{title}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
    <div className="mt-4 flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 font-medium">
      <ArrowDownTrayIcon className="h-4 w-4" /> Download
    </div>
  </button>
);

// ── Import Tab ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;

const ImportTab = () => {
  const [phase, setPhase] = useState('pick'); // pick | preview | polling | done
  const [dragOver, setDragOver] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [parsed, setParsed] = useState([]); // [{url, title, description, tags, folder}]
  const [addFolderTags, setAddFolderTags] = useState(false);
  const [createCollections, setCreateCollections] = useState(false);
  const [reprocessDuplicates, setReprocessDuplicates] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState(''); // 'json' | 'html'
  const [rawText, setRawText] = useState('');
  const [importId, setImportId] = useState(null);
  const [jobTotal, setJobTotal] = useState(0);
  const [jobStatus, setJobStatus] = useState(null); // last polled job object
  const [submitError, setSubmitError] = useState(null);
  const [results, setResults] = useState({ imported: 0, skipped: 0, failed: 0, collections: 0 });
  const fileRef = useRef();

  const processFile = useCallback((file) => {
    setParseError(null);
    setFileName(file.name);
    const isHtml = file.name.match(/\.html?$/i);
    const isJson = file.name.match(/\.json$/i);
    if (!isHtml && !isJson) {
      setParseError('Unsupported file type. Please upload a .json or .html bookmark file.');
      return;
    }
    setFileType(isHtml ? 'html' : 'json');
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      setRawText(text);
      const items = isHtml ? parseNetscapeHtml(text, addFolderTags) : parseJsonFile(text);
      if (!items) {
        setParseError("This file doesn't look like a bookmark export. Make sure it's a valid JSON or HTML bookmark file.");
        return;
      }
      if (items.length === 0) {
        setParseError('No bookmarks found in this file.');
        return;
      }
      setParsed(items);
      setPhase('preview');
    };
    reader.readAsText(file);
  }, [addFolderTags]);

  const handleFileInput = e => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  // Re-parse when addFolderTags changes while a file is loaded
  const handleFolderTagToggle = (checked) => {
    setAddFolderTags(checked);
    if (rawText && fileType === 'html') {
      const items = parseNetscapeHtml(rawText, checked);
      if (items) setParsed(items);
    }
  };

  const handleStartImport = async () => {
    setSubmitError(null);
    try {
      const { importId: id, total } = await bookmarkService.startImport({
        items: parsed,
        filename: fileName,
        reprocessDuplicates,
        createCollections,
      });
      setImportId(id);
      setJobTotal(total);
      setJobStatus(null);
      setPhase('polling');
    } catch (err) {
      setSubmitError(err.message || 'Failed to start import. Please try again.');
    }
  };

  // Poll for job status while in the polling phase
  useEffect(() => {
    if (phase !== 'polling' || !importId) return;

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const job = await bookmarkService.getImportJob(importId);
        if (cancelled) return;
        setJobStatus(job);

        if (job.status === 'completed') {
          // Build collections from the folderMap returned by the job (if requested)
          let collections = 0;
          if (createCollections && job.folderMap && Object.keys(job.folderMap).length > 0) {
            try {
              const { items: existingCollections } = await bookmarkService.listCollections();
              const byName = {};
              for (const c of existingCollections) byName[c.name.toLowerCase()] = c;

              for (const [folderName, bookmarkIds] of Object.entries(job.folderMap)) {
                if (!bookmarkIds.length) continue;
                try {
                  let col = byName[folderName.toLowerCase()];
                  if (!col) col = await bookmarkService.createCollection(folderName);
                  if (col?.collectionId) {
                    await bookmarkService.addBookmarksToCollection(col.collectionId, bookmarkIds);
                  }
                  collections++;
                } catch (_) { /* non-fatal */ }
              }
            } catch (_) { /* non-fatal */ }
          }

          saveHistoryEntry({
            type: 'import',
            filename: fileName,
            imported: job.added || 0,
            skipped: job.skipped || 0,
            reprocessed: 0,
            failed: job.failed || 0,
            collections,
          });
          setResults({ imported: job.added || 0, skipped: job.skipped || 0, failed: job.failed || 0, collections });
          setPhase('done');
        }
      } catch (_) {
        // Ignore transient errors — keep polling
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [phase, importId, createCollections, fileName]);

  const handleReset = () => {
    setPhase('pick');
    setParsed([]);
    setRawText('');
    setFileName('');
    setFileType('');
    setParseError(null);
    setSubmitError(null);
    setImportId(null);
    setJobStatus(null);
    setJobTotal(0);
    setResults({ imported: 0, skipped: 0, failed: 0, collections: 0 });
  };

  // ── Pick phase ──
  if (phase === 'pick') {
    return (
      <div className="space-y-5">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Import bookmarks from a browser export (<span className="font-mono text-xs">.html</span>) or a previous BookMarker backup (<span className="font-mono text-xs">.json</span>).
        </p>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
        >
          <ArrowUpTrayIcon className="h-10 w-10 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Drop a file here, or click to browse</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Supports .json (BookMarker) and .html (Chrome, Firefox, Safari)</p>
          <input ref={fileRef} type="file" accept=".json,.html,.htm" onChange={handleFileInput} className="hidden" />
        </div>

        {parseError && (
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {parseError}
          </div>
        )}

        {/* How to export from browsers */}
        <details className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
          <summary className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors">How to export bookmarks from your browser</summary>
          <ul className="mt-2 space-y-1 pl-4 list-disc">
            <li><span className="font-medium">Chrome:</span> Bookmarks menu → Bookmark manager → ⋮ → Export bookmarks</li>
            <li><span className="font-medium">Firefox:</span> Bookmarks menu → Manage bookmarks → Import and Backup → Export Bookmarks to HTML</li>
            <li><span className="font-medium">Safari:</span> File menu → Export → Bookmarks</li>
          </ul>
        </details>
      </div>
    );
  }

  // ── Preview phase ──
  if (phase === 'preview') {
    const preview = parsed.slice(0, 5);
    const remainder = parsed.length - preview.length;
    return (
      <div className="space-y-5">
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-5 py-4">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Found {parsed.length} bookmark{parsed.length !== 1 ? 's' : ''} in <span className="font-mono font-normal text-gray-600 dark:text-gray-400">{fileName}</span>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Duplicates will be skipped automatically.</p>
        </div>

        <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          {preview.map((b, i) => (
            <li key={i} className="px-4 py-3 bg-white dark:bg-gray-800">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{b.title || b.url}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{b.url}</p>
              {b.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {b.tags.slice(0, 4).map(t => (
                    <span key={t} className="px-1.5 py-0.5 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs">{t}</span>
                  ))}
                </div>
              )}
            </li>
          ))}
          {remainder > 0 && (
            <li className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400">
              …and {remainder} more
            </li>
          )}
        </ul>

        {/* HTML-only options: folder tags and collection creation */}
        {fileType === 'html' && (
          <div className="space-y-3">
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 dark:text-gray-300 select-none">
              <input
                type="checkbox"
                checked={addFolderTags}
                onChange={e => handleFolderTagToggle(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Add folder names as tags
              <span className="text-xs text-gray-400 dark:text-gray-500">(e.g. a "Work" folder becomes a <span className="font-mono">work</span> tag)</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 dark:text-gray-300 select-none">
              <input
                type="checkbox"
                checked={createCollections}
                onChange={e => setCreateCollections(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Create collections from folders
              <span className="text-xs text-gray-400 dark:text-gray-500">(each folder becomes a collection in BookMarker)</span>
            </label>
          </div>
        )}

        {/* Re-process option — available for all file types */}
        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-700 dark:text-gray-300 select-none">
          <input
            type="checkbox"
            checked={reprocessDuplicates}
            onChange={e => setReprocessDuplicates(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
          />
          Re-process bookmarks already in your library
          <span className="text-xs text-gray-400 dark:text-gray-500">(re-triggers AI processing for any duplicates instead of skipping them)</span>
        </label>

        {submitError && (
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {submitError}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={handleStartImport} className="btn-primary">
            Start Import
          </button>
          <button onClick={handleReset} className="btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Polling phase ──
  if (phase === 'polling') {
    const processed = jobStatus?.processed ?? 0;
    const total = jobStatus?.total ?? jobTotal;
    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
    return (
      <div className="space-y-5">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Importing… {processed} of {total}
        </p>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
          <div
            className="bg-primary-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Bookmarks are being saved in the background. You can navigate away — your import will continue and finish automatically.
        </p>
      </div>
    );
  }

  // ── Done phase ──
  const total = results.imported + results.skipped + results.failed;
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Import complete</h2>

      <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <ResultRow icon={<CheckCircleIcon className="h-5 w-5 text-green-500" />} label="Imported" value={results.imported} color="text-green-700 dark:text-green-400" />
        {results.skipped > 0 && (
          <ResultRow icon={<MinusCircleIcon className="h-5 w-5 text-gray-400" />} label="Skipped (already in your library)" value={results.skipped} color="text-gray-600 dark:text-gray-400" />
        )}
        {results.failed > 0 && (
          <ResultRow icon={<XCircleIcon className="h-5 w-5 text-red-400" />} label="Failed to import" value={results.failed} color="text-red-600 dark:text-red-400" />
        )}
        {results.collections > 0 && (
          <ResultRow icon={<CheckCircleIcon className="h-5 w-5 text-primary-500" />} label="Collections created from folders" value={results.collections} color="text-primary-700 dark:text-primary-400" />
        )}
      </div>

      {results.imported > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Bookmarks are being processed in the background — titles, summaries, and tags will appear shortly.
        </p>
      )}

      {results.failed > 0 && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {results.failed} bookmark{results.failed !== 1 ? 's' : ''} could not be saved (duplicate detection, invalid URLs, or other errors).{' '}
          <Link to="/bookmarks" state={{ filterStatus: 'failed' }} className="underline hover:text-red-800 dark:hover:text-red-300">
            View failed bookmarks
          </Link>{' '}
          to retry or remove them.
        </p>
      )}

      {total > 0 && results.imported === 0 && results.failed === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">All bookmarks were already in your library.</p>
      )}

      <div className="flex gap-3">
        <Link to="/bookmarks" className="btn-primary">
          View My Bookmarks
        </Link>
        <button onClick={handleReset} className="btn-secondary">
          Import Another File
        </button>
      </div>
    </div>
  );
};

// ── History Tab ───────────────────────────────────────────────────────────────

const HistoryTab = () => {
  const [history, setHistory] = useState(() => loadHistory());

  const handleClear = () => {
    clearHistory();
    setHistory([]);
  };

  if (history.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400 dark:text-gray-500">
        <ClockIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No import or export activity yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">{history.length} event{history.length !== 1 ? 's' : ''}</p>
        <button
          onClick={handleClear}
          className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
        >
          <TrashIcon className="h-3.5 w-3.5" /> Clear history
        </button>
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        {history.map((entry, i) => (
          <li key={i} className="px-5 py-3.5 bg-white dark:bg-gray-800 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`mt-0.5 flex-shrink-0 rounded-full p-1 ${entry.type === 'import' ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-500' : 'bg-green-50 dark:bg-green-900/30 text-green-500'}`}>
                {entry.type === 'import'
                  ? <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                  : <ArrowDownTrayIcon className="h-3.5 w-3.5" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                  {entry.type}
                  {entry.filename && <span className="font-normal text-gray-500 dark:text-gray-400"> — {entry.filename}</span>}
                  {entry.format && <span className="font-normal text-gray-500 dark:text-gray-400"> — {entry.format.toUpperCase()}</span>}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {entry.type === 'export' && `${entry.count} bookmark${entry.count !== 1 ? 's' : ''}`}
                  {entry.type === 'import' && [
                    entry.imported > 0 && `${entry.imported} imported`,
                    entry.reprocessed > 0 && `${entry.reprocessed} re-processed`,
                    entry.skipped > 0 && `${entry.skipped} skipped`,
                    entry.failed > 0 && `${entry.failed} failed`,
                    entry.collections > 0 && `${entry.collections} collection${entry.collections !== 1 ? 's' : ''}`,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5">{formatTimestamp(entry.timestamp)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ResultRow = ({ icon, label, value, color }) => (
  <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-800">
    <div className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
      {icon}
      {label}
    </div>
    <span className={`text-sm font-semibold ${color}`}>{value}</span>
  </div>
);

export default ImportExport;
